import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AfterSalesLayout from "../aftersaleslayout/AfterSalesLayout";
import FallbackImg from "../../../components/FallbackImg";
import BackBlue from "../../../assets/aftersalesservice_icons/back_blue_icon.svg";
import CalendarIcon from "../../../assets/aftersalesservice_icons/calendar.svg";
import InfoIcon from "../../../assets/aftersalesservice_icons/info_icon.svg";
import SolvedIcon from "../../../assets/aftersalesservice_icons/solved.svg";
import PersonIcon from "../../../assets/aftersalesservice_icons/person.svg";
import PhoneIcon from "../../../assets/aftersalesservice_icons/phone_black.svg";
import MailIcon from "../../../assets/aftersalesservice_icons/mail.svg";
import TicketIconNew from "../../../assets/aftersalesservice_icons/ticket_icon_new.svg";
import TicketIconOngoing from "../../../assets/aftersalesservice_icons/ticket_icon_ongoing.svg";
import TicketIconSolved from "../../../assets/aftersalesservice_icons/ticket_icon_solved.svg";
import ApiService from "../../../components/Api_services";
import { useLanguage } from "../LanguageContext";
import { getAfterSalesSerialType } from "../serialTypeUtils";

const MyTicketDetail = () => {
  const { trans } = useLanguage();
  const [searchParams] = useSearchParams();
  const ticketNo = searchParams.get("ticket_no") || "";
  const snParam = searchParams.get("sn") || "";
  const serialType = getAfterSalesSerialType(searchParams);

  const [ticketDetail, setTicketDetail] = useState(null);

  // ── helpers ────────────────────────────────────────────────────────────────

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}h ${m}m ${s}s`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString("en-MY", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const BADGE_COLOR = {
    new: "#5D5D5D",
    ongoing: "#FF6900",
    solved: "#00C950",
  };

  const TICKET_ICON = {
    new: TicketIconNew,
    ongoing: TicketIconOngoing,
    solved: TicketIconSolved,
  };

  // ── sortActivity ───────────────────────────────────────────────────────────
  // 'created' type always last, then sort by status_code descending

  const sortActivity = (activity) => {
    if (!Array.isArray(activity)) return [];
    return [...activity].sort((a, b) => {
      const aIsCreated = a?.type === "created";
      const bIsCreated = b?.type === "created";
      if (aIsCreated && !bIsCreated) return 1;
      if (bIsCreated && !aIsCreated) return -1;
      return (Number(b?.status_code) || 0) - (Number(a?.status_code) || 0);
    });
  };

  // ── Main Component ─────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchSingleTicket = async () => {
      if (!ticketNo) return;

      const payload = {
        ticket_no: ticketNo,
        sn: snParam,
        serial_type: serialType,
      };

      try {
        const response = await ApiService.getSingleTicket(payload);

        if (response && response.status === true) {
          setTicketDetail(response.data);
        }
      } catch (err) {
        console.error("Error fetching single ticket:", err);
      }
    };

    fetchSingleTicket();
  }, [ticketNo, snParam, serialType]);

  // ── normalise detail fields ─────────────

  const header = ticketDetail?.header || {};
  const ticket = ticketDetail?.ticket || {};
  const contact = ticketDetail?.contact || {};
  const issues = ticketDetail?.issues || [];
  const activity = ticketDetail?.activity || [];

  const ticket_no = header?.ticket_no || ticket?.ticket_no || "";
  const main_status = header?.main_status || { group: "new", label: "New" };
  const status_group = main_status?.group || "new";
  const status_label = main_status?.label || "New";

  const ticket_type_line =
    ticket?.type && ticket.type !== ""
      ? ticket.type
      : ticket?.issue_title || "";

  const created_raw = ticket?.inserted_date || null;

  // Formatted created date
  let created_formatted = "";
  const firstAct = activity[0];
  if (firstAct?.date) {
    created_formatted = firstAct.date;
  } else if (ticket?.inserted_date) {
    created_formatted = formatDate(ticket.inserted_date);
  }

  const issue_title =
    ticket?.issue_title && ticket.issue_title !== ""
      ? ticket.issue_title
      : issues[0]?.issue_title || "";

  // Find resolved date from activity (status_code === 5)
  const resolvedAct = activity.find((a) => Number(a?.status_code) === 5);
  const resolved_date_formatted = resolvedAct?.date || null;

  const badgeColor = BADGE_COLOR[status_group] || BADGE_COLOR.new;
  const ticketIcon = TICKET_ICON[status_group] || TICKET_ICON.new;

  const sortedActivity = sortActivity(activity);

  // ── ResponseTimer (only shown for 'new') ───────────────────────────────────

  const ResponseTimer = ({ start }) => {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
      const id = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(id);
    }, []);

    let startTime = start ? new Date(start).getTime() : Date.now();
    if (isNaN(startTime)) startTime = Date.now();
    const diffSeconds = Math.max(0, Math.floor((now - startTime) / 1000));
    const FULL_DAY = 86400;
    const progress = Math.min((diffSeconds / FULL_DAY) * 100, 100);

    return (
      <div className="as-info-card">
        <div className="as-timer-card as-response-timer" data-start={start}>
          <div className="as-timer-header">
            <div className="as-timer-label">
              <span>⏱ {trans.response_timer}</span>
            </div>
            <span className="as-timer-value">{formatTime(diffSeconds)}</span>
          </div>
          <div className="as-progress-bar-container">
            <div className="as-progress-bar">
              <div
                className="as-progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          <div className="as-timer-message">{trans.our_team}</div>
        </div>
      </div>
    );
  };

  return (
    <AfterSalesLayout>
      <div className="as-pre_page">
        <div className="as-page">
          <div className="as-post_page">
            {/* ── Header Card ── */}
            <div className="as-header-card">
              <button
                className="as-back-btn"
                onClick={() => window.history.back()}
                aria-label="Go back"
              >
                <FallbackImg src={BackBlue} alt="back" />
                <span>{trans.back}</span>
              </button>

              <div className="as-ticket-header">
                <FallbackImg
                  src={ticketIcon}
                  alt="Ticket Icon"
                  className="as-ticket-icon"
                />
                <div className="as-ticket-header-content">
                  <div className="as-ticket-header-top">
                    <span className="as-ticket-number">#{ticket_no}</span>
                    <span
                      className={`as-status-badge ${status_group}`}
                      style={{
                        background: badgeColor,
                        color: "#fff",
                        padding: "0.25rem 0.75rem",
                        borderRadius: 16,
                        fontWeight: 500,
                        fontSize: "0.813rem",
                      }}
                    >
                      {status_label}
                    </span>
                  </div>
                  <div className="as-ticket-title">{ticket_type_line}</div>
                </div>
              </div>
            </div>

            {/* ── Response Timer (new only) ── */}
            {status_group === "new" && <ResponseTimer start={created_raw} />}

            {/* ── Ticket Information Card ── */}
            <div className="as-info-card">
              <h3 className="as-card-section-title">{trans.ticket_info}</h3>

              {/* Created date */}
              <div className="as-info-row">
                <FallbackImg
                  src={CalendarIcon}
                  alt="Calendar"
                  className="as-info-icon"
                />
                <div className="as-info-content">
                  <div className="as-info-label">{trans.ticket_created}</div>
                  <div className="as-info-value">{created_formatted}</div>
                </div>
              </div>

              {/* Resolved date (solved only) */}
              {status_group === "solved" && resolved_date_formatted && (
                <div
                  className="as-info-row"
                  style={{ background: "#F0FDF4", borderRadius: 12 }}
                >
                  <FallbackImg
                    src={SolvedIcon}
                    alt="Resolved"
                    className="as-info-icon"
                    style={{
                      filter:
                        "brightness(0) saturate(100%) invert(63%) sepia(93%) saturate(449%) hue-rotate(95deg) brightness(96%) contrast(101%)",
                    }}
                  />
                  <div className="as-info-content">
                    <div
                      className="as-info-label"
                      style={{ color: "#00A63E", fontWeight: 600 }}
                    >
                      {trans.ticket_resolved}
                    </div>
                    <div
                      className="as-info-value"
                      style={{ color: "#0D542B", fontWeight: 600 }}
                    >
                      {resolved_date_formatted}
                    </div>
                  </div>
                </div>
              )}

              {/* Issue title */}
              <div className="as-info-row">
                <FallbackImg
                  src={InfoIcon}
                  alt="Issue"
                  className="as-info-icon"
                />
                <div className="as-info-content">
                  <div className="as-info-label">{trans.issue_title}</div>
                  <div className="as-info-value">{issue_title}</div>
                </div>
              </div>
            </div>

            {/* ── Contact Details Card ── */}
            <div className="as-info-card">
              <h3 className="as-card-section-title">{trans.contact_details}</h3>

              <div className="as-contact-row">
                <FallbackImg
                  src={PersonIcon}
                  alt="Person"
                  className="as-contact-icon"
                />
                <div className="as-contact-content">
                  <div className="as-contact-label">{trans.name}</div>
                  <div className="as-contact-value">{contact?.name || ""}</div>
                </div>
              </div>

              <div className="as-contact-row">
                <FallbackImg
                  src={PhoneIcon}
                  alt="Phone"
                  className="as-contact-icon"
                />
                <div className="as-contact-content">
                  <div className="as-contact-label">{trans.mobile_number}</div>
                  <div className="as-contact-value">{contact?.phone || ""}</div>
                </div>
              </div>

              <div className="as-contact-row">
                <FallbackImg
                  src={MailIcon}
                  alt="Email"
                  className="as-contact-icon"
                />
                <div className="as-contact-content">
                  <div className="as-contact-label">{trans.email}</div>
                  <div className="as-contact-value">{contact?.email || ""}</div>
                </div>
              </div>
            </div>

            {/* ── Activity Timeline Card ── */}
            <div className="as-info-card">
              <h3 className="as-card-section-title">
                {trans.activity_timeline}
              </h3>

              {sortedActivity.length > 0 ? (
                sortedActivity.map((act, index) => {
                  const atype = act?.type || "status";
                  const title = act?.title || "";
                  const rawRemark = act?.remark || "";
                  const date = act?.date || act?.inserted_date || "";

                  let descriptionText;
                  if (atype === "created") {
                    descriptionText = trans.activity_timeline_sub_1;
                  } else {
                    const remarkDisplay =
                      rawRemark.trim() !== "" ? rawRemark.trim() : "--";
                    descriptionText = `${trans.remark}${remarkDisplay}`;
                  }

                  // First item = blue dot, rest = gray
                  const dotColor = index === 0 ? "#0057D8" : "#D1D5DC";

                  return (
                    <div className="as-timeline-item" key={index}>
                      <div
                        className="as-timeline-dot"
                        style={{ background: dotColor }}
                      />
                      <div className="as-timeline-content">
                        <div className="as-timeline-title">{title}</div>
                        {descriptionText && (
                          <div className="as-timeline-description">
                            {descriptionText}
                          </div>
                        )}
                        <div className="as-timeline-time">
                          <svg
                            className="as-time-icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="9"
                              stroke="#999"
                              strokeWidth="2"
                            />
                            <path
                              d="M12 7V12L15 15"
                              stroke="#999"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                          <span>{date}</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="as-timeline-item">
                  <div className="as-timeline-dot" />
                  <div className="as-timeline-content">
                    <div className="as-timeline-title">{trans.no_activity}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AfterSalesLayout>
  );
};

export default MyTicketDetail;
